// #genai Phase 2 — a single saved-payment-method row. Pixel-matched to Figma 219:24003 (collapsed)
// and 239:7554 (expanded billing detail). Cards and bank accounts use the same row; the secondary
// line, brand icon and the "Name on …" label differ by type.
import React, { useState, useEffect } from 'lib/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import { EVTypography, EVButton, EVKeyboardArrowDown } from 'lib/components';
import {
  ACH_ACCOUNT_TYPE_IDS,
  ACH_CORPORATE_CHECKING_TYPE,
  PAYMENT_METHOD_OPTION_IDS,
} from 'commonComponents/paymentInformation/PaymentInformationConstants';
import { PHONE_REGEX_WITHOUT_HYPHEN } from 'constants/AppConstants';
import useMediaQuery from 'hooks/useMediaQuery';
import PaymentMethodBrandIcon from './PaymentMethodBrandIcon.component';
import SavedPaymentMethodsStyles from './SavedPaymentMethodsStyles';

// #genai ACH account-type code → secondary-line i18n key (mirrors PHASE2_ACH_ACCOUNT_TYPE_OPTIONS).
const ACH_TYPE_LABEL_KEYS = {
  [ACH_ACCOUNT_TYPE_IDS.Checking]: 'billing_information__pm_checking_account',
  [ACH_ACCOUNT_TYPE_IDS.Savings]: 'billing_information__pm_savings_account',
  [ACH_CORPORATE_CHECKING_TYPE]: 'billing_information__pm_corporate_checking_account',
};

// #genai SLN-5064: display the phone number formatted as "(XXX) XXX-XXXX" to match the Figma
// (never the extension). A 10-digit number (any incoming separators) is reformatted; anything else
// falls back to the raw value. Empty -> '--'. Reuses PHONE_REGEX_WITHOUT_HYPHEN (/^\d{10}$/) so the
// digit-count rule stays in one place with the rest of the app's phone validation.
const formatPhone = (phone) => {
  if (!phone) return '--';
  // #genai replaceAll (global regex) over replace per SonarQube S7781; behavior is identical.
  const digits = String(phone).replaceAll(/\D/g, '');
  if (PHONE_REGEX_WITHOUT_HYPHEN.test(digits)) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

function DetailItem({
  label, children, classes, itemClassName,
}) {
  return (
    <div className={itemClassName || classes.detailItem}>
      <EVTypography className={classes.detailLabel}>{label}</EVTypography>
      <div className={classes.detailValue}>{children}</div>
    </div>
  );
}

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  itemClassName: PropTypes.string,
};

DetailItem.defaultProps = {
  itemClassName: '',
};

function SavedPaymentMethodRow({
  method, onRemove, onMakeDefault, isPending, collapseNonce,
}) {
  const { t } = useTranslation();
  const classes = SavedPaymentMethodsStyles();
  const isMobile = useMediaQuery(640);
  const [isExpanded, setIsExpanded] = useState(false);

  // #genai Parent bumps collapseNonce on Make Default — collapse every open row (this + siblings).
  useEffect(() => {
    if (collapseNonce > 0) {
      setIsExpanded(false);
    }
  }, [collapseNonce]);

  const isAch = method.type === PAYMENT_METHOD_OPTION_IDS.Ach;
  // #genai Phase 2 expired-card state (Figma 269:20146 / 269:20163): card-only flag that swaps the
  // DEFAULT pill for an EXPIRED pill, dims the row background, and hides the "Make Default" link
  // (an expired card cannot be promoted). "Remove" + chevron stay available.
  const isExpired = !isAch && Boolean(method.isExpired);
  const secondaryLine = isAch
    ? t(ACH_TYPE_LABEL_KEYS[method.accountType] || 'billing_information__pm_checking_account')
    : t('billing_information__pm_expires', { date: method.expiration });
  const nameLabel = isAch
    ? t('billing_information__pm_name_on_account')
    : t('billing_information__pm_name_on_card');

  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const billingAddress = (method.billingAddressLines || []).map((line) => (
    <div key={line}>{line}</div>
  ));

  // #genai Mobile (Figma 239:7271 / 239:7101): tap the header to toggle so the chevron can be hidden.
  const headerInteractionProps = isMobile
    ? {
      role: 'button',
      tabIndex: 0,
      onClick: toggleExpanded,
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleExpanded();
        }
      },
    }
    : {};

  // #genai Figma 269:20146 — the EXPIRED row uses the page-chrome bg (#F0F2F7), drops the white card
  // shadow, and shows the EXPIRED pill where DEFAULT would normally sit. Active rows keep the white +
  // shadow treatment unchanged.
  const rowClassName = isExpired ? `${classes.row} ${classes.rowExpired}` : classes.row;
  // #genai Status pill slot: at most one of DEFAULT / EXPIRED is shown (expired wins over default —
  // an expired card should never be the active default, per Figma which never pairs the two).
  const statusBadge = isExpired
    ? (
      <span className={classes.expiredBadge}>
        {t('billing_information__pm_expired_badge')}
      </span>
    )
    : method.isDefault && (
      <span className={classes.defaultBadge}>
        {t('billing_information__pm_default_badge')}
      </span>
    );

  return (
    <div className={rowClassName}>
      <div className={classes.rowHeader} {...headerInteractionProps}>
        <div className={classes.rowIconText}>
          <PaymentMethodBrandIcon type={method.type} brand={method.brand} />
          <div className={classes.rowTextBlock}>
            <div className={classes.endingRow}>
              <EVTypography className={classes.endingText}>
                {t('billing_information__pm_ending_in', { lastFour: method.lastFour })}
              </EVTypography>
              {/* #genai Desktop badge sits next to the text; mobile badge is right-aligned (below). */}
              {!isMobile && statusBadge}
            </div>
            <EVTypography className={classes.secondaryText}>{secondaryLine}</EVTypography>
          </div>
        </div>

        {isMobile ? (
          statusBadge
        ) : (
          <div className={classes.rowActions}>
            {/* #genai Hide "Make Default" for the active default row AND for expired cards
                (Figma 269:20146 — only "Remove" + chevron are shown on the expired row). */}
            {!method.isDefault && !isExpired && (
              <button
                type="button"
                className={classes.makeDefault}
                disabled={isPending}
                onClick={() => onMakeDefault(method)}
              >
                {t('billing_information__pm_make_default')}
              </button>
            )}
            <button
              type="button"
              className={classes.remove}
              disabled={isPending}
              onClick={() => onRemove(method)}
            >
              {t('billing_information__pm_remove')}
            </button>
            <button
              type="button"
              className={classes.chevronBtn}
              aria-expanded={isExpanded}
              aria-label={t('billing_information__pm_toggle_details')}
              onClick={toggleExpanded}
            >
              <EVKeyboardArrowDown
                className={`${classes.chevronIcon} ${isExpanded ? classes.chevronExpanded : ''}`}
              />
            </button>
          </div>
        )}
      </div>

      {isExpanded && isMobile && (
        <div className={classes.detailMobile}>
          {/* #genai Per Figma 239:7271 vs 239:7101, mobile shows "Name" for cards only; bank
              accounts (ACH) hide the Name row. */}
          {!isAch && (
            <DetailItem label={t('billing_information__pm_name')} classes={classes} itemClassName={classes.detailItemMobile}>
              {method.nameOnAccount}
            </DetailItem>
          )}
          <DetailItem label={t('billing_information__pm_billing_address')} classes={classes} itemClassName={classes.detailItemMobile}>
            {billingAddress}
          </DetailItem>
          <DetailItem label={t('billing_information__pm_email')} classes={classes} itemClassName={classes.detailItemMobile}>
            {method.billingEmail}
          </DetailItem>
          <DetailItem label={t('billing_information__pm_company')} classes={classes} itemClassName={classes.detailItemMobile}>
            {method.company || '--'}
          </DetailItem>
          <DetailItem label={t('billing_information__pm_country')} classes={classes} itemClassName={classes.detailItemMobile}>
            {method.country}
          </DetailItem>
          <DetailItem label={t('billing_information__pm_phone')} classes={classes} itemClassName={classes.detailItemMobile}>
            {/* #genai SLN-5064: show phone number only (no extension) per Figma */}
            {formatPhone(method.phone)}
          </DetailItem>
          <DetailItem label={t('billing_information__pm_secondary_phone')} classes={classes} itemClassName={classes.detailItemMobile}>
            {/* #genai SLN-5064: secondary phone now sourced from billingAddress; number only, no ext */}
            {formatPhone(method.secondaryPhone)}
          </DetailItem>

          <div className={classes.mobileActions}>
            {/* #genai Same hide rule as desktop — expired cards never expose the make-default CTA. */}
            {!method.isDefault && !isExpired && (
              <EVButton
                variant="contained"
                className={classes.makeDefaultBtnMobile}
                disabled={isPending}
                onClick={() => onMakeDefault(method)}
              >
                {t('billing_information__pm_make_default_payment')}
              </EVButton>
            )}
            <EVButton
              variant="contained"
              className={classes.removeBtnMobile}
              disabled={isPending}
              onClick={() => onRemove(method)}
            >
              {t('billing_information__pm_remove')}
            </EVButton>
          </div>
        </div>
      )}

      {isExpanded && !isMobile && (
        <div className={classes.detail}>
          <div className={classes.detailCol}>
            <DetailItem label={nameLabel} classes={classes}>
              {method.nameOnAccount}
            </DetailItem>
            <DetailItem label={t('billing_information__pm_billing_address')} classes={classes}>
              {billingAddress}
            </DetailItem>
            <DetailItem label={t('billing_information__pm_country')} classes={classes}>
              {method.country}
            </DetailItem>
          </div>
          <div className={classes.detailCol}>
            <DetailItem label={t('billing_information__pm_company')} classes={classes}>
              {method.company || '--'}
            </DetailItem>
            <DetailItem label={t('billing_information__pm_billing_email')} classes={classes}>
              {method.billingEmail}
            </DetailItem>
            <DetailItem label={t('billing_information__pm_phone')} classes={classes}>
              {/* #genai SLN-5064: show phone number only (no extension) per Figma */}
              {formatPhone(method.phone)}
            </DetailItem>
            <DetailItem label={t('billing_information__pm_secondary_phone')} classes={classes}>
              {/* #genai SLN-5064: secondary phone now sourced from billingAddress; number only, no ext */}
              {formatPhone(method.secondaryPhone)}
            </DetailItem>
          </div>
        </div>
      )}
    </div>
  );
}

SavedPaymentMethodRow.propTypes = {
  method: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    apiType: PropTypes.oneOf(['credit-card', 'ach']),
    type: PropTypes.oneOf(['ach', 'card']).isRequired,
    lastFour: PropTypes.string.isRequired,
    isDefault: PropTypes.bool,
    isExpired: PropTypes.bool,
    brand: PropTypes.string,
    expiration: PropTypes.string,
    accountType: PropTypes.string,
    nameOnAccount: PropTypes.string,
    company: PropTypes.string,
    billingAddressLines: PropTypes.arrayOf(PropTypes.string),
    country: PropTypes.string,
    billingEmail: PropTypes.string,
    phone: PropTypes.string,
    phoneExt: PropTypes.string, // #genai SLN-5064
    secondaryPhone: PropTypes.string,
    secondaryExt: PropTypes.string, // #genai SLN-5064
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onMakeDefault: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
  // #genai Incremented by SavedPaymentMethods on Make Default to collapse all expanded rows.
  collapseNonce: PropTypes.number,
};

SavedPaymentMethodRow.defaultProps = {
  isPending: false,
  collapseNonce: 0,
};

export default SavedPaymentMethodRow;
